import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { DefaultComponentProps } from '../types';

type RerenderType = ((callback?: () => void) => void) | undefined;

type LayoutProviderType = {
  rerender: RerenderType;
  setRerender: (func: RerenderType) => void;
  carouselVisibleIndexes: number[];
  setCarouselVisibleIndexes(value: number[]): void;
};

const LayoutContext = createContext<LayoutProviderType>({
  rerender: undefined,
  carouselVisibleIndexes: [],
  setRerender: () => {},
  setCarouselVisibleIndexes: () => {}
});

export function LayoutProvider({ children }: DefaultComponentProps) {
  const [carouselVisibleIndexes, setCarouselVisibleIndexes] = useState<
    number[]
  >([]);
  const [rerender, setRerender] = useState<RerenderType>(undefined);

  const setRerenderCallback = useCallback(
    (func: RerenderType) => setRerender(() => func),
    [setRerender]
  );

  const layoutContext = useMemo<LayoutProviderType>(
    () => ({
      carouselVisibleIndexes,
      setCarouselVisibleIndexes,
      rerender,
      setRerender: setRerenderCallback
    }),
    [carouselVisibleIndexes, rerender, setRerenderCallback]
  );

  return (
    <LayoutContext.Provider value={layoutContext}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  return useContext(LayoutContext);
}
